import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import ReportRunPage from './ReportRunPage';
import { REPORT_CONFIGS } from './reportConfigs';

/**
 * Dynamic Report Runner - loads report configuration from database
 * and renders the appropriate report page
 */
const DynamicReportRunner = () => {
  const { reportType } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const reportConfig = REPORT_CONFIGS[reportType];

  useEffect(() => {
    fetchReportConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const fetchReportConfig = async () => {
    setIsLoading(true);
    setError(false);
    
    try {
      // Fetch all reports and find the matching one
      const response = await axiosPrivate.get('/api/system-reports');
      const reports = response.data || [];
      const matchingReport = reports.find(r => r.ReportType === reportType);

      if (!matchingReport) {
        setError(true);
        enqueueSnackbar('Report not found', { variant: 'error' });
        return;
      }

      setReport(matchingReport);
    } catch (err) {
      console.error('Failed to fetch report configuration:', err);
      setError(true);
      enqueueSnackbar('Failed to load report', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!reportConfig) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Report Configuration Not Found</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12">
                <div className="alert alert-danger" role="alert">
                  The report configuration for "{reportType}" could not be found.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Loading Report...</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 text-center">
                <CircularProgress />
                <p className="mt-3">Loading report configuration...</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !report) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Report Not Found</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12">
                <div className="alert alert-danger" role="alert">
                  The requested report could not be found.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <ReportRunPage
      reportName={report.ReportName}
      reportDescription={report.ReportDescription}
      reportConfig={reportConfig}
    />
  );
};

export default DynamicReportRunner;
